import type FieldId from "../fields/FieldId";
import { isNil } from "lodash-es";
import type { Navigator } from "@solidjs/router";
import { useNavigate, useSearchParams } from "@solidjs/router";
import { initialized } from "../utils/initializedUtil";
import FieldService from "./FieldService";
import { getSearchQuery } from "../utils/uriUtil";
import SubformShowEvent from "../events/SubformShowEvent";
import type RuleId from "../fields/RuleId";
import RuleService from "./RuleService";
import RuleShowEvent from "../events/RuleShowEvent";
import type IntegrationId from "../integrations/IntegrationId";
import IntegrationShowEvent from "../events/IntegrationShowEvent";
import IntegrationService from "./IntegrationService";
import type { SearchParams } from "../types/Router";
import { Scope } from "../types";
import { useCustomerState } from "../state/customer";
import { useFormState } from "../state/form";
import FormVersionRestoreService from "./FormVersionRestoreService";
import VariableService from "./VariableService";
import type VariableId from "../variables/VariableId";
import VariableShowEvent from "../events/VariableShowEvent";
import { scrollToField } from "../utils/fieldUtil";
import FieldShowEvent from "../events/FieldShowEvent";

let navigateInstance: Navigator | null = null;
let searchParamsInstance: Partial<SearchParams> | null = null;
let customerInstance: ReturnType<typeof useCustomerState> | null = null;
let formInstance: ReturnType<typeof useFormState> | null = null;

const init = () => {
  navigateInstance = useNavigate();
  searchParamsInstance = useSearchParams()[0];
  customerInstance = useCustomerState();
  formInstance = useFormState();
};

const navigate = () => initialized(navigateInstance, "navigate");
const searchParams = () => initialized(searchParamsInstance, "searchParams");
const customer = () => initialized(customerInstance, "customer");
const form = () => initialized(formInstance, "form");

export default {
  init,
  exitFormEditor: () => {
    FormVersionRestoreService.cleanup();

    // If the editor has been opened in a new tab (e.g. via the old platform), then close the tab. Otherwise, just
    // redirect to the form overview page.
    if (!isNil(window.opener)) {
      window.close();
    }
    // Browsers refuse to close a tab that wasn't opened by a script (e.g. the platform itself was opened in a new tab
    // from another page), in which case the window stays open
    if (!window.closed) {
      navigate()(`/customers/${customer().customerId}/forms/${form().id}`);
    }
  },
  navigateToField: (id: FieldId, behaviour: "smooth" | "instant" = "smooth") => {
    const field = FieldService.getField(id);

    if (!isNil(field)) {
      if (field.scope === Scope.ROOT) {
        navigate()(`../fields${getSearchQuery(searchParams())}`, { replace: true });
        new FieldShowEvent(field.uid).send(); // Open side panel
        scrollToField(field.uid, behaviour);
      } else {
        new SubformShowEvent(field.scope, "fields").send(); // Open subform drawer
        new FieldShowEvent(field.uid).send(); // Open side panel
        scrollToField(field.uid, behaviour);
      }
    }
  },
  navigateToVariable: (id: VariableId) => {
    const variable = VariableService.getVariable(id);

    if (!isNil(variable)) {
      navigate()(`../variables${getSearchQuery(searchParams())}`, {
        replace: true,
      });
      new VariableShowEvent(variable.uid).send();
    }
  },
  navigateToRule: (id: RuleId) => {
    const rule = RuleService.get(id);

    if (!isNil(rule)) {
      if (rule.scope === Scope.ROOT) {
        navigate()(`../rules${getSearchQuery(searchParams())}`, {
          replace: true,
        });
        new RuleShowEvent(rule.id).send();
      } else {
        new SubformShowEvent(rule.scope, "rules").send();
        new RuleShowEvent(rule.id).send();
      }
    }
  },
  navigateToIntegration: (id: IntegrationId) => {
    const integration = IntegrationService.getIntegration(id);

    if (!isNil(integration)) {
      navigate()(`../integrations${getSearchQuery(searchParams())}`, {
        replace: true,
      });
      new IntegrationShowEvent(id).send();
    }
  },
};
